import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
	getProjectEvaluations,
	getFreelancerProfileEvaluations,
	submitProjectEvaluation,
	moderateProjectEvaluation,
	editProjectEvaluation,
	searchProjectEvaluations
} from "../api/project-evaluations-api";
import {
	ProjectEvaluation,
	FreelancerProfileEvaluationSummary,
	SubmitProjectEvaluationRequest,
	ModerateProjectEvaluationRequest,
	UpdateProjectEvaluationRequest,
	ProjectEvaluationSearchResult,
	ProjectEvaluationStatus,
	EvaluatorType
} from "../models/ProjectEvaluation";
import { useSnackbarContext } from "../contexts/useSnackBarContext";
import { useLinearProgressContext } from "../contexts/useLinearProgressContext";

/**
 * Hook to get evaluations for a project
 */
export const useProjectEvaluations = (projectId: number, enabled: boolean = true) => {
	return useQuery<ProjectEvaluation[], Error>({
		queryKey: ["project-evaluations", "project", projectId],
		queryFn: () => getProjectEvaluations(projectId),
		enabled: enabled && projectId > 0
	});
};

/**
 * Hook to get freelancer profile evaluation summary
 */
export const useFreelancerProfileEvaluations = (
	freelancerProfileId: number,
	enabled: boolean = true,
	evaluatorType?: EvaluatorType
) => {
	return useQuery<FreelancerProfileEvaluationSummary, Error>({
		queryKey: ["project-evaluations", "freelancer-profile", freelancerProfileId, evaluatorType],
		queryFn: () => getFreelancerProfileEvaluations(freelancerProfileId, evaluatorType),
		enabled: enabled && freelancerProfileId > 0
	});
};

/**
 * Hook to submit an evaluation
 */
export const useSubmitProjectEvaluation = () => {
	const queryClient = useQueryClient();
	const { showSuccess, showError } = useSnackbarContext();
	const { showProgress, hideProgress } = useLinearProgressContext();
	const { t } = useTranslation();

	return useMutation<
		ProjectEvaluation,
		Error,
		{ id: number; request: SubmitProjectEvaluationRequest }
	>({
		mutationFn: ({ id, request }) => {
			showProgress();
			return submitProjectEvaluation(id, request);
		},
		onSuccess: () => {
			hideProgress();
			showSuccess(t("evaluations.submitSuccess"));
			queryClient.invalidateQueries({ queryKey: ["project-evaluations"] });
		},
		onError: (error: Error) => {
			hideProgress();
			console.error("Error submitting evaluation:", error);
			showError(t("evaluations.submitError"));
		}
	});
};

/**
 * Hook to moderate an evaluation (CSM)
 */
export const useModerateProjectEvaluation = () => {
	const queryClient = useQueryClient();
	const { showSuccess, showError } = useSnackbarContext();
	const { showProgress, hideProgress } = useLinearProgressContext();
	const { t } = useTranslation();

	return useMutation<
		ProjectEvaluation,
		Error,
		{ id: number; request: ModerateProjectEvaluationRequest }
	>({
		mutationFn: ({ id, request }) => {
			showProgress();
			return moderateProjectEvaluation(id, request);
		},
		onSuccess: () => {
			hideProgress();
			showSuccess(t("evaluations.moderateSuccess"));
			queryClient.invalidateQueries({ queryKey: ["project-evaluations"] });
		},
		onError: (error: Error) => {
			hideProgress();
			console.error("Error moderating evaluation:", error);
			showError(t("evaluations.moderateError"));
		}
	});
};

/**
 * Hook to edit an evaluation (CSM)
 */
export const useEditProjectEvaluation = () => {
	const queryClient = useQueryClient();
	const { showSuccess, showError } = useSnackbarContext();
	const { showProgress, hideProgress } = useLinearProgressContext();
	const { t } = useTranslation();

	return useMutation<
		ProjectEvaluation,
		Error,
		{ id: number; request: UpdateProjectEvaluationRequest }
	>({
		mutationFn: ({ id, request }) => {
			showProgress();
			return editProjectEvaluation(id, request);
		},
		onSuccess: () => {
			hideProgress();
			showSuccess(t("evaluations.editSuccess"));
			queryClient.invalidateQueries({ queryKey: ["project-evaluations"] });
		},
		onError: (error: Error) => {
			hideProgress();
			console.error("Error editing evaluation:", error);
			showError(t("evaluations.editError"));
		}
	});
};

/**
 * Hook to search evaluations (CSM)
 */
export const useSearchProjectEvaluations = (
	page: number = 1,
	pageSize: number = 20,
	status?: ProjectEvaluationStatus,
	evaluatorType?: EvaluatorType,
	enabled: boolean = true
) => {
	return useQuery<ProjectEvaluationSearchResult, Error>({
		queryKey: ["project-evaluations", "search", page, pageSize, status, evaluatorType],
		queryFn: () => searchProjectEvaluations(page, pageSize, status, evaluatorType),
		enabled
	});
};
